import type { HandId } from '@store/handStore';

export type BucketHand = HandId | 'both';

export interface CalibrationBucket {
  id: string;
  hand: BucketHand;
  prompt: string;
  /** Short concrete instruction — "6–9 reps", "hold still", "full sweep 2×". */
  reps: string;
  durationSec: number;
}

const SPEEDS = ['slow', 'medium', 'fast'] as const;
const FINGERS = ['thumb', 'index', 'middle'] as const;

/** Target rep counts per 3s take, so the user knows how hard to push. */
const PLUCK_REPS: Record<(typeof SPEEDS)[number], string> = {
  slow: '~3 reps (1 per second)',
  medium: '~6 reps (2 per second)',
  fast: '~9+ reps (3+ per second)',
};
const GLISS_REPS: Record<(typeof SPEEDS)[number], string> = {
  slow: '1 full sweep across all strings',
  medium: '2 full sweeps',
  fast: '3–4 full sweeps',
};
const BEND_REPS: Record<'still' | 'gentle_vibrato' | 'fast_vibrato', string> = {
  still: 'hold perfectly still, no motion',
  gentle_vibrato: 'gentle vibrato, ~2 oscillations/sec',
  fast_vibrato: 'fast vibrato, ~5 oscillations/sec',
};

function forHand(hand: HandId): CalibrationBucket[] {
  const label = hand === 'left' ? 'LEFT' : 'RIGHT';
  const out: CalibrationBucket[] = [
    {
      id: `${hand}_play_stance`,
      hand,
      prompt: `${label}: play stance — palm facing, 3 fingers extended`,
      reps: 'hold still in a comfortable ready-to-play pose',
      durationSec: 3,
    },
    {
      id: `${hand}_not_play`,
      hand,
      prompt: `${label}: NOT play — wave, point, rest, anything that should stay idle`,
      reps: 'keep changing — vary the pose throughout the 3s',
      durationSec: 3,
    },
  ];
  for (const finger of FINGERS) {
    for (const speed of SPEEDS) {
      out.push({
        id: `${hand}_pluck_${finger}_${speed}`,
        hand,
        prompt: `${label}: pluck ${finger} — ${speed}`,
        reps: PLUCK_REPS[speed],
        durationSec: 3,
      });
    }
  }
  for (const finger of FINGERS) {
    for (const speed of SPEEDS) {
      out.push({
        id: `${hand}_gliss_${finger}_${speed}`,
        hand,
        prompt: `${label}: gliss ${finger} across strings — ${speed}`,
        reps: GLISS_REPS[speed],
        durationSec: 3,
      });
    }
  }
  for (const speed of ['still', 'gentle_vibrato', 'fast_vibrato'] as const) {
    out.push({
      id: `${hand}_bend_${speed}`,
      hand,
      prompt: `${label}: bend — ${speed.replace('_', ' ')}`,
      reps: BEND_REPS[speed],
      durationSec: 3,
    });
  }
  out.push(
    {
      id: `${hand}_mute_accept`,
      hand,
      prompt: `${label}: mute ACCEPTED — tight fist, try palm-facing AND sideways`,
      reps: 'hold tight fist; rotate through a few orientations',
      durationSec: 3,
    },
    {
      id: `${hand}_mute_reject`,
      hand,
      prompt: `${label}: mute REJECTED — loose fist / cupped / half-curl (should NOT mute)`,
      reps: 'cycle through near-misses: loose fist, cupped bend, half-curl',
      durationSec: 3,
    },
  );
  return out;
}

/**
 * Full calibration timeline. ~51 buckets, ~155s of recording.
 */
export const CALIBRATION_SCRIPT: readonly CalibrationBucket[] = [
  ...forHand('left'),
  ...forHand('right'),
  {
    id: 'both_bend',
    hand: 'both',
    prompt: 'BOTH hands: bend together',
    reps: 'both hands in bend posture simultaneously',
    durationSec: 4,
  },
];

/** Gap between buckets so the user can re-pose. */
export const BUCKET_REST_SEC = 2;

/**
 * Focused thumb-gliss dynamics calibration — 10 buckets, each 3 s.
 * Collects the full curl-to-extension range (quặp / rest / soft / medium
 * / loud) per hand so thresholds for both the curl GATE and the dynamics
 * mapping can be fit to real data for the user's specific thumb flex.
 * Much shorter than the full script (~60 s of recording vs ~155 s) so
 * it's cheap to re-run.
 */
const THUMB_DYNAMICS = [
  { key: 'quap', prompt: 'QUẶP (curled, NO gliss)', reps: 'thumb curled toward palm — hold still, should NOT fire' },
  { key: 'rest', prompt: 'THẢ LỎNG (relaxed, NO gliss)', reps: 'thumb at natural play-stance rest — hand idle' },
  { key: 'soft', prompt: 'NHẸ (soft gliss)', reps: 'thumb extended, very light sweep — just barely sounds' },
  { key: 'medium', prompt: 'VỪA (medium gliss)', reps: 'thumb extended, clear sweep — clear but not strong' },
  { key: 'loud', prompt: 'TO (loud gliss)', reps: 'thumb fully extended, full strong sweep' },
] as const;

function thumbDynamicsForHand(hand: HandId): CalibrationBucket[] {
  const label = hand === 'left' ? 'LEFT' : 'RIGHT';
  return THUMB_DYNAMICS.map(({ key, prompt, reps }) => ({
    id: `${hand}_thumb_gliss_${key}`,
    hand,
    prompt: `${label} thumb gliss — ${prompt}`,
    reps,
    durationSec: 3,
  }));
}

export const THUMB_GLISS_DYNAMICS_SCRIPT: readonly CalibrationBucket[] = [
  ...thumbDynamicsForHand('left'),
  ...thumbDynamicsForHand('right'),
];

/** Catalog of available scripts — keyed so CalibrationWizard can pick. */
export const CALIBRATION_SCRIPTS = {
  full: CALIBRATION_SCRIPT,
  thumb_dynamics: THUMB_GLISS_DYNAMICS_SCRIPT,
} as const;

export type CalibrationScriptKey = keyof typeof CALIBRATION_SCRIPTS;
